'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useScheduleWriteAuthority } from './useScheduleWriteAuthority';
export interface CopySource { schedule_id: string; user_id: string; is_public?: boolean; name?: string; term_id?: string; classes?: readonly string[]; configuration?: unknown }
export interface CopyPayload { name: string; term_id: string; is_public: false; classes: string[]; configuration: unknown }
export interface CopySnapshot {
  actorId: string | null; source: CopySource | null; fallbackTerm: string;
  isViewingSelectedSchedule: boolean; generatedEligible: boolean; ownedDraftReady: boolean;
  visibleOption: { courses: readonly { baseId: string; id: string }[] } | null;
  currentConfiguration: object | null;
}
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
/** Capture the viewed option and current owned configuration, never replace foreign source data. */
export type CopyIntentSnapshot = Omit<CopySnapshot, 'ownedDraftReady' | 'generatedEligible'>;
export function describeScheduleCopyPayload(input: CopyIntentSnapshot): CopyPayload | null {
  const { source } = input;
  if (!source) return null;
  const own = source.user_id === input.actorId;
  if (!own && source.is_public !== true) return null;
  if (own && !input.isViewingSelectedSchedule && !input.visibleOption) return null;
  const configuration = own ? input.currentConfiguration && { ...input.currentConfiguration, currentScheduleIndex: 0 }
    : source.configuration || { selectedCourses: [], pinnedClasses: [], currentScheduleIndex: 0, selectedCourseIds: [], customActivities: [] };
  if (own && !configuration) return null;
  return clone({ name: `${source.name || 'Schedule'} (Copy)`, term_id: source.term_id || input.fallbackTerm,
    is_public: false, classes: own && !input.isViewingSelectedSchedule
      ? input.visibleOption!.courses.map(course => `${course.baseId}:${course.id}`) : [...(source.classes ?? [])], configuration });
}
/** Dispatch eligibility is separate from representing a captured copy intent. */
export function buildScheduleCopyPayload(input: CopySnapshot): CopyPayload | null {
  const own = input.source?.user_id === input.actorId;
  if (own && (!input.ownedDraftReady || (!input.isViewingSelectedSchedule && !input.generatedEligible))) return null;
  return describeScheduleCopyPayload(input);
}

interface Options {
  ownerId: string | null; sourceId: string | null; sourceOwnerId: string | null;
  routeKey: string; term: string; draft: unknown; payload: CopyPayload | null;
  isRouteCurrent: () => boolean;
  createCopy: (payload: CopyPayload) => Promise<{ schedule_id?: string } | null>;
  onSignInRequired: () => void;
  /** Current execution availability, checked only before a new physical attempt. */
  canDispatch?: () => boolean;
}
type Status = 'idle' | 'creating' | 'confirmed' | 'unconfirmed' | 'invalid';
interface Receipt { context: object; revision: object; status: Status; copyId: string | null; error: string | null }
const idle = (context: object, revision: object): Receipt => ({ context, revision, status: 'idle', copyId: null, error: null });
interface Checkpoint { context: object; payloadKey: string; copyId: string | null; unconfirmed: boolean }

/** Only create a copy: no source update, profile write, navigation or retry executor. */
export function useScheduleCopy(options: Options) {
  const { ownerId, sourceId, sourceOwnerId, routeKey, term, draft } = options;
  const context = useMemo(() => ({ ownerId, sourceId, sourceOwnerId, routeKey, term }), [ownerId, sourceId, sourceOwnerId, routeKey, term]);
  const payloadKey = JSON.stringify(options.payload);
  const revision = useMemo(() => ({ draft, payloadKey }), [draft, payloadKey]);
  // scheduleId identifies the read source here; the only mutation is createCopy.
  const authority = useScheduleWriteAuthority({ ownerId, scheduleId: sourceId,
    routeKey: JSON.stringify([routeKey, sourceOwnerId]), term, draft: revision });
  const committed = useRef<{ context: typeof context; revision: typeof revision; options: Options } | null>(null);
  const checkpoint = useRef<Checkpoint | null>(null);
  const [state, setState] = useState<Receipt>(() => idle(context, revision));
  useLayoutEffect(() => { committed.current = { context, revision, options }; });
  useLayoutEffect(() => {
    checkpoint.current = null;
    return () => { checkpoint.current = null; };
  }, [context]);
  useLayoutEffect(() => { setState(idle(context, revision)); }, [context, revision]);

  const copy = useCallback(async (): Promise<boolean> => {
    const current = committed.current;
    const isRenderedCurrent = () => committed.current?.context === context && committed.current.revision === revision
      && authority.isContextCurrent() && committed.current.options.isRouteCurrent();
    if (!current || !isRenderedCurrent() || !sourceId) return false;
    if (!ownerId) { current.options.onSignInRequired(); return false; }
    if (!current.options.payload || JSON.stringify(current.options.payload) !== payloadKey) {
      setState(previous => isRenderedCurrent() ? { ...idle(context, revision), status: 'invalid', error: 'Choose a current schedule option before creating a copy.' } : previous);
      return false;
    }
    if (current.options.canDispatch && !current.options.canDispatch()) return false;
    const payload = clone(current.options.payload);
    const operation = authority.begin();
    if (!operation) return false;
    const isCurrent = () => isRenderedCurrent() && operation.isCurrent();
    const isReceiptCurrent = () => isRenderedCurrent() && operation.isLifetimeCurrent();
    const publish = (status: Status, copyId: string | null, error: string | null) =>
      setState(previous => isReceiptCurrent() ? { context, revision, status, copyId, error } : previous);
    let saved = checkpoint.current;
    if (!saved || saved.context !== context || saved.payloadKey !== payloadKey) {
      saved = { context, payloadKey, copyId: null, unconfirmed: false }; checkpoint.current = saved;
    }
    try {
      if (!isCurrent()) return false;
      if (saved.unconfirmed && !saved.copyId) {
        publish('unconfirmed', null, 'Copy could not be confirmed. Check your saved schedules before starting another copy.'); return false;
      }
      if (!saved.copyId) {
        publish('creating', null, null); saved.unconfirmed = true;
        const created = await current.options.createCopy(clone(payload));
        if (!created || typeof created.schedule_id !== 'string' || !created.schedule_id.trim()) throw new Error('Unconfirmed copy');
        saved.copyId = created.schedule_id; saved.unconfirmed = false;
      }
      if (!isCurrent() || checkpoint.current !== saved) return false;
      publish('confirmed', saved.copyId, null); return true;
    } catch {
      if (isCurrent() && checkpoint.current === saved) publish('unconfirmed', null, 'Copy could not be confirmed. Check your saved schedules before starting another copy.');
      return false;
    } finally { operation.finish(); }
  }, [context, revision, authority.begin, authority.isContextCurrent, ownerId, sourceId, payloadKey]);
  const receipt = state.context === context && state.revision === revision ? state : idle(context, revision);
  return { copy, isPending: authority.isPending, status: receipt.status, copyId: receipt.copyId, error: receipt.error };
}
